import { HighlightOff } from "@mui/icons-material"
import React, { FC } from "react"

import {
  AdvancedForm,
  AdvancedFormData,
  AdvancedFormFieldProps,
  AdvancedForms,
} from "src/components/common/advancedForm"
import { Button } from "src/components/common/button"
import { Drawer } from "src/components/common/drawer"
import { IconButton } from "src/components/common/iconButton"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"

import { create } from "src/helpers/bem"
import { postAccount } from "src/queriesXRM/account"
import { useAccount } from "src/states/account"

import { mergeAccountForm, useAccountForm } from "src/states/accountForm"
import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"
import { TranslationMessages } from "src/translations"

import { xRMApiAccount } from "src/types/xRM"

import styles from "./EditProfileDialog.module.scss"

const bem = create(styles, "EditProfileDialog")

export type EditProfileDialogProps = {
  messages: TranslationMessages
  style: BasicLayoutStyleProps
}

export const EditProfileDialog: FC<EditProfileDialogProps> = ({
  messages,
  style,
}) => {
  const profileMessages = messages.pages.account.overview.profile
  const account = useAccount()
  const form = useAccountForm()
  const [loading, setLoading] = React.useState<boolean>(false)

  const formIndex = form.findIndex((form) => form.id === "personals")
  const filteredForm = form
    .filter((form) => form.id === "personals")
    .map((form) => {
      const fields = form.fields.map((field) => {
        return { ...field }
      })
      return { ...form, fields }
    })
  const formData: AdvancedFormData = [...filteredForm] as AdvancedForms[]
  const { open, unsaved } = useDialog(
    "editProfileDialog",
  ) as Dialogs["editProfileDialog"]

  const closeDialog = () => {
    mergeDialog("editProfileDialog", { open: false })
  }

  const mergeFormToAccount = async () => {
    const firstName = formData[0].fields
      .find((field) => field.name === "firstName")
      ?.value?.toString()
    const lastName = formData[0].fields
      .find((field) => field.name === "lastName")
      ?.value?.toString()
    const phone = formData[0].fields
      .find((field) => field.name === "phone")
      ?.value?.toString()
    const language = formData[0].fields.find(
      (field) => field.name === "language",
    )?.value as number
    const salutation = formData[0].fields
      .find((field) => field.name === "salutation")
      ?.value?.toString()

    const update = {
      ...account,
      firstName,
      lastName,
      phone,
      language,
      salutation,
    }
    await postAccount(update as xRMApiAccount)
    mergeDialog("editProfileDialog", { unsaved: false })
  }

  const handleMerge = (
    index: number,
    items: AdvancedFormFieldProps,
    name: string | null,
  ) => {
    if (!unsaved) mergeDialog("editProfileDialog", { unsaved: true })
    mergeAccountForm(formIndex, items, name)
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      await mergeFormToAccount()
      setLoading(false)
      mergeDialog("editProfileDialog", { open: false })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      style={style}
      title={profileMessages.form.title}
      onClose={() => closeDialog()}
      onOpen={() => mergeDialog("editProfileDialog", { open: true })}
    >
      <div className={bem()}>
        <div className={bem("header")}>
          <h2>{profileMessages.form.title}</h2>
          <div className={bem("close")}>
            <IconButton
              color={"primary"}
              icon={HighlightOff}
              onClick={() => {
                closeDialog()
              }}
            />
          </div>
        </div>
        <div className={bem("form")}>
          <AdvancedForm
            data={formData}
            formMessages={profileMessages.form}
            handleMergeField={handleMerge}
            messages={messages}
          ></AdvancedForm>
        </div>
        <div className={bem("footer")}>
          {unsaved && (
            <h3 className={bem("footer__info")}>
              {profileMessages.form.unsavedChanges}
            </h3>
          )}
          <Button
            buttonType="primary"
            loading={loading}
            title={profileMessages.form.saveButton}
            onClick={handleSave}
          />
        </div>
      </div>
    </Drawer>
  )
}
